/* ---------------------------------------------------------------------------
   When scheduled content is on air
   ---------------------------------------------------------------------------
   Donations, announcements and posters each carry a display start / end. The
   backend decides which of them belong to the day a screen is currently showing
   (its own timezone, rolled over at the evening zman on a Hebrew calendar), and
   hands the display that whole day at once — a screen only refetches every 30
   minutes, so waiting for a refresh would make a 3:02 PM announcement turn up
   anywhere up to half an hour late. These helpers do the last step on the
   display itself: reveal each item the minute its start time actually arrives.

   Reading a stored date needs one piece of history. `displayStartDate` /
   `displayEndDate` are `timestamptz` columns, but what the save path stores is
   the admin's WALL-CLOCK time stamped as UTC: the modal sends an offset-aware
   ISO string and the announcement / donation / weekly-event services subtract
   that same offset again. So `2026-08-31T15:02:00Z` means "3:02 PM", not "11:02
   in New York" — which is exactly how the admin UI reads it back (see
   toWallClockDate). Posters are the one exception: their service saves the ISO
   string untouched, so a poster's stored value really is a true instant.
   -------------------------------------------------------------------------- */

export interface DisplayScheduleOptions {
  // Poster-style storage: the stored value is already an absolute instant and
  // must not be re-read as a wall-clock time.
  storedAsInstant?: boolean;
}

const DATE_TIME_PARTS_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?/;

// The wall-clock reading of a stored value: its own year/month/day/hour/minute,
// with no timezone applied. Mirrors the admin UI's toWallClockDate.
const getStoredWallClockParts = (value: unknown): number[] | null => {
  if (!value) return null;

  if (typeof value === 'string') {
    const match = value.trim().match(DATE_TIME_PARTS_RE);
    if (match) {
      return [
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        match[4] == null ? 0 : Number(match[4]),
        match[5] == null ? 0 : Number(match[5]),
        match[6] == null ? 0 : Number(match[6]),
        match[7] == null ? 0 : Number(String(match[7]).padEnd(3, '0')),
      ];
    }
  }

  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return null;
  // A Date carries no wall-clock string, so take the UTC face of it — that is
  // the face the save path wrote and the admin UI reads.
  return [
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds(),
  ];
};

// How far `timeZone` is ahead of UTC at `instant`, in milliseconds.
export const getZoneOffsetMs = (instant: number, timeZone: string): number => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(instant));

    const map: Record<string, string> = {};
    parts.forEach((part) => { map[part.type] = part.value; });

    const asUtc = Date.UTC(
      Number(map.year),
      Number(map.month) - 1,
      Number(map.day),
      Number(map.hour),
      Number(map.minute),
      Number(map.second),
    );

    return Number.isFinite(asUtc) ? asUtc - instant : 0;
  } catch (error) {
    return 0;
  }
};

const resolveScheduleInstant = (
  value: unknown,
  timeZone: string,
  options: DisplayScheduleOptions,
): Date | null => {
  if (options.storedAsInstant) {
    const date = value instanceof Date ? value : new Date(String(value ?? ''));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const parts = getStoredWallClockParts(value);
  if (!parts) return null;

  const naiveUtc = Date.UTC(parts[0], parts[1], parts[2], parts[3], parts[4], parts[5], parts[6]);
  if (!timeZone) return new Date(naiveUtc);

  // Two passes so a wall-clock time that sits either side of a DST change still
  // resolves to the instant that clock face actually happens at.
  const firstPass = naiveUtc - getZoneOffsetMs(naiveUtc, timeZone);
  return new Date(naiveUtc - getZoneOffsetMs(firstPass, timeZone));
};

// Resolving a stored date builds an Intl formatter, and the display re-checks
// every scheduled item once a second, so memoise it: a given stored value always
// names the same instant in a given timezone.
const instantCache = new Map<string, Date | null>();
const INSTANT_CACHE_LIMIT = 500;

// The instant a wall-clock reading names on a screen in `timeZone`.
export const getScheduleInstant = (
  value: unknown,
  timeZone: string,
  options: DisplayScheduleOptions = {},
): Date | null => {
  const cacheKey = `${timeZone}|${options.storedAsInstant ? 'i' : 'w'}|${
    value instanceof Date ? value.toISOString() : String(value)
  }`;
  const cached = instantCache.get(cacheKey);
  if (cached !== undefined) return cached;

  const resolved = resolveScheduleInstant(value, timeZone, options);
  if (instantCache.size >= INSTANT_CACHE_LIMIT) instantCache.clear();
  instantCache.set(cacheKey, resolved);
  return resolved;
};

// Has this item's display start time been reached on a screen in `timeZone`?
// Anything unreadable stays visible: the backend already decided it belongs to
// today, and a parsing edge case must never blank content that should be up.
export const hasScheduleStarted = (
  value: unknown,
  timeZone: string,
  now: Date | number = Date.now(),
  options: DisplayScheduleOptions = {},
): boolean => {
  const startsAt = getScheduleInstant(value, timeZone, options);
  if (!startsAt) return true;

  const nowMillis = now instanceof Date ? now.getTime() : Number(now);
  if (!Number.isFinite(nowMillis)) return true;

  return startsAt.getTime() <= nowMillis;
};

// Has this item's display end time passed? Same forgiving reading as above: an
// unreadable or absent end date means no end boundary at all.
export const hasScheduleEnded = (
  value: unknown,
  timeZone: string,
  now: Date | number = Date.now(),
  options: DisplayScheduleOptions = {},
): boolean => {
  const endsAt = getScheduleInstant(value, timeZone, options);
  if (!endsAt) return false;

  const nowMillis = now instanceof Date ? now.getTime() : Number(now);
  if (!Number.isFinite(nowMillis)) return false;

  return endsAt.getTime() < nowMillis;
};
